import React, { useState } from 'react';

import { type UploadedFile } from '@/lib/files/service';

import { getFileIcon } from './utils/getFileIcon';

interface InlineFileCardProps {
  file: UploadedFile;
  onDownload?: () => void;
}

export const InlineFileCard: React.FC<InlineFileCardProps> = ({
  file,
  onDownload,
}) => {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const isImage = file.mime_type.startsWith('image/');
  const fileSizeMB = (file.file_size / (1024 * 1024)).toFixed(2);

  const handleDownload = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    if (onDownload) {
      onDownload();
    } else if (file.download_url) {
      // Create a temporary anchor to trigger download
      const link = document.createElement('a');
      link.href = file.download_url;
      link.download = file.original_filename;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    }
  };

  const handleCardClick = () => {
    if (isImage) {
      // For images, open modal
      setIsModalOpen(true);
    } else {
      // For non-images, open in new tab
      if (onDownload) {
        onDownload();
      } else if (file.download_url) {
        window.open(file.download_url, '_blank');
      }
    }
  };

  return (
    <>
      <div
        onClick={handleCardClick}
        className='inline-flex items-center gap-2 px-3 py-2 bg-bg-primary rounded-lg border border-border-secondary hover:border-border-primary transition-colors shadow-sm hover:shadow-md cursor-pointer'
        style={{ maxWidth: '280px' }}
      >
        {isImage && file.file_url ? (
          <div className='flex-shrink-0 size-10 rounded overflow-hidden bg-bg-secondary border border-border-secondary'>
            <img
              src={file.file_url}
              alt={file.original_filename}
              className='size-full object-cover'
            />
          </div>
        ) : (
          <div className='flex-shrink-0'>
            {getFileIcon(file.original_filename, file.mime_type, { size: 40 })}
          </div>
        )}
        <div className='flex-1 min-w-0'>
          <div className='text-xs text-fg-primary truncate font-medium'>
            {file.original_filename}
          </div>
          <div className='text-[10px] text-fg-quaternary'>{fileSizeMB} MB</div>
        </div>
        <button
          onClick={handleDownload}
          className='flex-shrink-0 p-1.5 text-fg-quaternary hover:text-fg-primary hover:bg-bg-secondary rounded transition-colors'
          title='Download file'
        >
          <svg
            className='size-4'
            fill='none'
            strokeWidth={2}
            viewBox='0 0 24 24'
            stroke='currentColor'
          >
            <path
              strokeLinecap='round'
              strokeLinejoin='round'
              d='M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z'
            />
          </svg>
        </button>
      </div>

      {/* Image Modal */}
      {isModalOpen && isImage && file.file_url && (
        <div
          className='fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm'
          onClick={() => setIsModalOpen(false)}
        >
          <div
            className='relative max-w-7xl max-h-[90vh] m-4'
            onClick={e => e.stopPropagation()}
          >
            {/* Close button */}
            <button
              onClick={() => setIsModalOpen(false)}
              className='absolute -top-10 right-0 p-2 text-white hover:text-gray-300 transition-colors cursor-pointer'
              aria-label='Close'
            >
              <svg
                className='size-6'
                fill='none'
                viewBox='0 0 24 24'
                stroke='currentColor'
                strokeWidth={2}
              >
                <path
                  strokeLinecap='round'
                  strokeLinejoin='round'
                  d='M6 18L18 6M6 6l12 12'
                />
              </svg>
            </button>

            {/* Image */}
            <img
              src={file.file_url}
              alt={file.original_filename}
              className='max-w-full max-h-[90vh] rounded-lg shadow-2xl'
            />

            {/* File info bar at bottom */}
            <div className='absolute bottom-0 left-0 right-0 bg-black/70 text-white px-4 py-3 rounded-b-lg'>
              <div className='flex items-center justify-between gap-4'>
                <div className='flex-1 min-w-0'>
                  <p className='text-sm font-medium truncate'>
                    {file.original_filename}
                  </p>
                  <p className='text-xs text-gray-300'>{fileSizeMB} MB</p>
                </div>
                <button
                  onClick={handleDownload}
                  className='flex items-center gap-2 px-3 py-1.5 bg-white/10 hover:bg-white/20 rounded-md transition-colors text-sm'
                >
                  <svg
                    className='size-4'
                    fill='none'
                    strokeWidth={2}
                    viewBox='0 0 24 24'
                    stroke='currentColor'
                  >
                    <path
                      strokeLinecap='round'
                      strokeLinejoin='round'
                      d='M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z'
                    />
                  </svg>
                  Download
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
};
